import React, { useState, useRef } from 'react';
import { exportChartToExcel, ChartData } from '../utils/chartDetection';

// Table export wrapper - adds hover-to-reveal Excel export button on each table
const TableExportWrapper: React.FC<{ children: React.ReactNode; node?: any }> = ({ children, node, ...props }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!tableRef.current) return;
    setIsExporting(true);
    try {
      const headers: string[] = [];
      const rows: Record<string, string>[] = [];

      const thElements = tableRef.current.querySelectorAll('thead th');
      thElements.forEach(th => headers.push(th.textContent?.trim() || ''));

      // If no thead, try first row
      if (headers.length === 0) {
        const firstRow = tableRef.current.querySelector('tr');
        if (firstRow) {
          firstRow.querySelectorAll('th, td').forEach(cell => headers.push(cell.textContent?.trim() || ''));
        }
      }

      const bodyRows = tableRef.current.querySelectorAll('tbody tr');
      bodyRows.forEach(tr => {
        const row: Record<string, string> = {};
        tr.querySelectorAll('td').forEach((td, i) => {
          row[headers[i] || `Column ${i + 1}`] = td.textContent?.trim() || '';
        });
        rows.push(row);
      });

      // Fallback: if no tbody, grab all rows except the first (header)
      if (rows.length === 0) {
        const allRows = tableRef.current.querySelectorAll('tr');
        allRows.forEach((tr, rowIdx) => {
          if (rowIdx === 0) return; // skip header row
          const row: Record<string, string> = {};
          tr.querySelectorAll('td, th').forEach((cell, i) => {
            row[headers[i] || `Column ${i + 1}`] = cell.textContent?.trim() || '';
          });
          rows.push(row);
        });
      }

      const chartData: ChartData = {
        type: 'table',
        data: rows,
        title: `table_export_${new Date().toISOString().slice(0, 10)}`
      };

      await exportChartToExcel(chartData);
    } catch (error) {
      console.error('Table export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="table-export-container relative group my-4">
      <table ref={tableRef} {...props}>{children}</table>
      <button
        onClick={handleExport}
        disabled={isExporting}
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-900/60 shadow-sm cursor-pointer z-10"
        title="Export table to Excel"
      >
        {isExporting ? (
          <div className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        )}
        Excel
      </button>
    </div>
  );
};

// Shared ReactMarkdown components override - adds Excel export to every table
export const markdownTableComponents = {
  table: ({ children, node, ...props }: any) => (
    <TableExportWrapper node={node} {...props}>{children}</TableExportWrapper>
  ),
};

export default TableExportWrapper;
